import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import PopularBadge from "@/components/common/PopularBadge";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

const ASSETS = ["USDT", "BTC"];
const SIZE_PILLS = [
  { label: "₹10L", value: 1_000_000 },
  { label: "₹1Cr", value: 10_000_000 },
  { label: "₹5Cr", value: 50_000_000 },
  { label: "₹10Cr", value: 100_000_000 },
];

function formatINR(v) {
  if (!v) return "";
  return Number(v).toLocaleString("en-IN");
}

export default function OtcInquiryModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    asset: "USDT",
    side: "buy",
    estimated_size_inr: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        mobile: "",
        asset: "USDT",
        side: "buy",
        estimated_size_inr: "",
        notes: "",
      });
      setErr("");
      setSuccess(false);
    }
  }, [open]);

  useBodyScrollLock(open);

  if (!open) return null;

  function set(field, v) {
    setForm((f) => ({ ...f, [field]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = {
        ...form,
        estimated_size_inr: Number(form.estimated_size_inr),
      };
      await api.post("/api/otc/inquiry", payload);
      setSuccess(true);
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="cm-modal-overlay bg-cm-navy/70 backdrop-blur-sm cm-fade-up"
      onClick={onClose}
      data-testid="otc-inquiry-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="cm-modal-panel bg-white max-w-[560px] rounded-[20px] p-5 sm:p-8 relative overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-cm-grey hover:text-cm-navy"
          aria-label="Close"
          data-testid="otc-modal-close"
        >
          <X size={20} />
        </button>

        {success ? (
          <div className="text-center py-8" data-testid="otc-inquiry-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-5">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h3 className="text-[22px] font-bold text-cm-navy">We've got your request</h3>
            <p className="mt-3 text-[14px] text-cm-grey leading-relaxed">
              Our OTC desk will reach out to you within{" "}
              <strong className="text-cm-navy">1 business hour</strong> on{" "}
              <strong className="text-cm-navy">{form.email}</strong>.
            </p>
            <button
              onClick={onClose}
              className="mt-7 inline-flex items-center justify-center h-[48px] px-7 rounded-[12px] bg-cm-blue text-white font-semibold hover:bg-cm-blue-deep transition"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6 pr-8">
              <span className="inline-flex items-center gap-2">
                <PopularBadge testId="otc-modal-popular-badge" />
                <span className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase text-cm-blue bg-blue-50 px-2.5 py-1 rounded">
                  OTC Desk
                </span>
              </span>
              <h3 className="mt-3 text-[24px] font-bold text-cm-navy leading-tight">
                Talk to our OTC desk
              </h3>
              <p className="mt-2 text-[13px] text-cm-grey">
                Buy or sell USDT & BTC in large size. Dedicated relationship manager — tell us about your trade.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {["buy", "sell"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("side", s)}
                    className={`py-2.5 rounded-[10px] text-[13px] font-bold capitalize ${
                      form.side === s
                        ? s === "buy"
                          ? "bg-emerald-600 text-white"
                          : "bg-red-500 text-white"
                        : "bg-cm-surface text-cm-grey"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full name *">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    data-testid="otc-name"
                    className="w-full h-[44px] px-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
                  />
                </Field>
                <Field label="Mobile *">
                  <input
                    required
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => set("mobile", e.target.value.replace(/[^\d+]/g, ""))}
                    placeholder="10-digit number"
                    maxLength={13}
                    data-testid="otc-mobile"
                    className="w-full h-[44px] px-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
                  />
                </Field>
              </div>

              <Field label="Email *">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="otc-email"
                  className="w-full h-[44px] px-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Asset">
                  <select
                    value={form.asset}
                    onChange={(e) => set("asset", e.target.value)}
                    data-testid="otc-asset"
                    className="w-full h-[44px] px-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none bg-white"
                  >
                    {ASSETS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Size (₹) *">
                  <input
                    required
                    type="text"
                    inputMode="numeric"
                    value={
                      form.estimated_size_inr
                        ? formatINR(form.estimated_size_inr)
                        : ""
                    }
                    onChange={(e) =>
                      set("estimated_size_inr", e.target.value.replace(/[^\d]/g, ""))
                    }
                    placeholder="e.g. 50,00,000"
                    data-testid="otc-size"
                    className="w-full h-[44px] px-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
                  />
                </Field>
              </div>

              <div className="flex flex-wrap gap-2">
                {SIZE_PILLS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => set("estimated_size_inr", String(p.value))}
                    data-testid={`otc-pill-${p.label}`}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                      String(form.estimated_size_inr) === String(p.value)
                        ? "bg-cm-navy text-white border-cm-navy"
                        : "bg-white text-cm-grey border-[var(--cm-border)] hover:text-cm-navy hover:border-cm-navy"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <Field label="Notes (optional)">
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={2}
                  data-testid="otc-notes"
                  className="w-full p-3 border border-[#E0E3EF] rounded-[10px] text-[14px] focus:border-cm-blue outline-none resize-none"
                  placeholder="Anything we should know? Timing, custodian, settlement preferences…"
                />
              </Field>

              {err && (
                <div className="flex items-start gap-2 text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="otc-submit"
                className="w-full h-[52px] rounded-[12px] bg-cm-navy text-white font-bold text-[15px] hover:bg-cm-blue transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Submit OTC"}
                {!loading && <ArrowRight size={16} />}
              </button>

              <p className="text-[11px] text-cm-grey text-center">
                By submitting you agree to be contacted by our OTC desk. We never share your details.
              </p>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-cm-navy mb-1.5">{label}</span>
      {children}
    </label>
  );
}
